import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { AuthorsList } from '../../articleComponents/author/AuthorsList';
import { H4, H1, semanticCSS } from '../../../typography/semanticTags';

const componentConfigurationKey = 'articleTitle';

interface ArticleTitleThemeProps {
  titleColor: string
  metaDataColor: string
  vrLineHeight: VrLineHeight
}

interface ArticleTitleDataProps {
  title: string
  by: string
  authors: AuthorsList
  updatedAtFormatted: string
  children?: React.ReactElement
}

type ArticleTitleProps = ArticleTitleThemeProps & ArticleTitleDataProps;

const createStyle = ({ titleColor, metaDataColor, vrLineHeight }: ArticleTitleThemeProps) => StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  title: {
    color: titleColor,
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
    },
  },
  metaData: {
    color: metaDataColor,
  },
  sidesPadding: {
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      marginLeft: 0,
      marginRight: 0,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      marginLeft: 0,
      marginRight: 0,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
      marginRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
  },

});

const shouldDisplayAuthors = (authors: AuthorsList) => {
  return authors.length > 0;
};

export const ArticleTitleComponent: React.FunctionComponent<ArticleTitleProps> = (props: ArticleTitleProps) => {
  const { title, by, updatedAtFormatted, children, authors, metaDataColor } = props;
  const style = createStyle(props);
  const hasAuthors = shouldDisplayAuthors(authors);
  const separator = hasAuthors ? ' | ' : '';

  return (
    <React.Fragment>
      <H1 styles={semanticCSS(style.title, style.sidesPadding)}>{title}</H1>
      <div className={css(style.wrapper)}>
        <H4 styles={semanticCSS(style.metaData, style.sidesPadding)}>
          {hasAuthors && <AuthorsList by={by} authors={authors} color={metaDataColor} />}
          {`${separator}${updatedAtFormatted}`}
        </H4>
        {children}
      </div>
    </React.Fragment>
  );
};

const mapThemeToProps = (theme: ArticleTitleThemeProps) => ({
  titleColor: theme.titleColor,
  metaDataColor: theme.metaDataColor,
  vrLineHeight: theme.vrLineHeight,
});

export const ArticleTitle = (props: ArticleTitleDataProps) => {
  const themeContext = useTheme(mapThemeToProps, componentConfigurationKey);
  return <ErrorBoundary><ArticleTitleComponent {...themeContext} {...props} /></ErrorBoundary>;
};
